import { DeviceType } from '../device/common/deviceTypes';

/** 외부 API 계약. 이미 배포된 값은 변경하거나 재사용하지 않습니다. */
export enum AppId {
  BELLCONNECT = 0,
  LIBUSCONNECT = 1,
}

export const DEFAULT_APP_ID = AppId.BELLCONNECT;
export const APP_IDS: readonly AppId[] = Object.values(AppId).filter(
  (value): value is AppId => typeof value === 'number'
);

export interface DeviceBranding {
  readonly displayName: string;
  readonly images: {
    readonly connected: string;
    readonly disconnected: string;
    readonly whole: string;
    readonly sensors?: Readonly<Record<string, string>>;
  };
}

export interface AppConfig {
  readonly displayName: string;
  readonly devices: Readonly<Record<DeviceType, DeviceBranding>>;
}

/** 앱별 이미지는 /images/<앱 폴더>/ 아래에 같은 파일명 규칙으로 둡니다. */
const MINI_SENSOR_NAMES = [
  'CO',
  'O2',
  'H2',
  'H2S',
  'NH3',
  'Cl2',
  'SO2',
  'NO2',
  'HCN',
  'PH3',
  'ETO',
  'O3',
  'HCl',
  'NO',
  'NH3-E',
  'CO-H',
  'CH2O2',
  'CO-E',
  'CO2',
] as const;

/** O2-LF만 파일명이 센서명과 다릅니다(bellmini_LF.png). */
function miniSensorImages(dir: string): Record<string, string> {
  return Object.fromEntries(
    MINI_SENSOR_NAMES.map(name => [name, `/images/${dir}/bellmini_${name}.png`]).concat([
      ['O2-LF', `/images/${dir}/bellmini_LF.png`],
    ])
  );
}

/** 앱별 표시 정책. 새로운 차이는 이 타입에 명시적으로 추가합니다. */
export const APP_CONFIGS: Readonly<Record<AppId, AppConfig>> = {
  [AppId.BELLCONNECT]: {
    displayName: 'BellConnect',
    devices: {
      [DeviceType.LITE]: {
        displayName: 'BELLLITE',
        images: {
          connected: '/images/bellconnect/belllite_connected.png',
          disconnected: '/images/bellconnect/belllite_whole.png',
          whole: '/images/bellconnect/belllite_whole.png',
        },
      },
      [DeviceType.MINI]: {
        displayName: 'BELLMINI',
        images: {
          connected: '/images/bellconnect/bellmini_CO.png',
          disconnected: '/images/bellconnect/bellmini_disconnect.png',
          whole: '/images/bellconnect/bellmini_CO.png',
          sensors: miniSensorImages('bellconnect'),
        },
      },
    },
  },
  [AppId.LIBUSCONNECT]: {
    displayName: 'LibusConnect',
    devices: {
      [DeviceType.LITE]: {
        // LITE는 연결 상태별 이미지가 없어 한 장을 공용으로 사용합니다.
        displayName: 'BELLLITE',
        images: {
          connected: '/images/libusconnect/belllite_whole.png',
          disconnected: '/images/libusconnect/belllite_whole.png',
          whole: '/images/libusconnect/belllite_whole.png',
        },
      },
      [DeviceType.MINI]: {
        displayName: 'BELLMINI',
        images: {
          connected: '/images/libusconnect/bellmini_CO.png',
          disconnected: '/images/libusconnect/bellmini_disconnect.png',
          whole: '/images/libusconnect/bellmini_CO.png',
          sensors: miniSensorImages('libusconnect'),
        },
      },
    },
  },
};

export function isAppId(value: unknown): value is AppId {
  return typeof value === 'number' && APP_IDS.includes(value);
}

/** 조회 시 구버전 데이터는 기본 앱으로 표시합니다. 요청 검증에는 사용하지 않습니다. */
export function getAppConfig(appId: AppId = DEFAULT_APP_ID): AppConfig {
  return APP_CONFIGS[appId] ?? APP_CONFIGS[DEFAULT_APP_ID];
}

export function getDeviceDisplayName(deviceType: string, appId?: AppId): string {
  return getAppConfig(appId).devices[deviceType as DeviceType]?.displayName ?? deviceType;
}

export interface AppRequest {
  /** 기존 클라이언트 호환: 생략하면 BellConnect. 새 클라이언트는 항상 전송합니다. */
  app_id?: AppId;
}
